import React from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { getUser } from "selectors/commonSelectors";
import { ADD_USER_MODAL } from "../../features/collaboration/UserModals";
import { useTrackEvent } from "libs/analytics";
import {
  HEADER_MENU_ADD_USER_CLICK,
  HEADER_ADD_USER_CLICK,
  UPGRADE_USERS_VIEWED,
} from "constants/Events";
import {
  Link,
  Trigger,
  DropdownMenuContent,
  DropdownText,
  DropdownBoldText,
  DropdownButton,
} from "./header";
import { Flex } from "ui/commonStyled/layout";
import { useOpenIntercomChat } from "libs/use-open-intercom-chat";
import UsersIcon from "./icons/usersIcon.svg";
import { MODAL_OPEN_FUNCTIONS } from "libs/modals/init";
import { goToManagePlan } from "utils/manage-plan";
import { isLegacyPlan } from "../../features/upsell/plan-utils";
import { getUserLimit } from "utils/plan-limits";
import { openSupportChat } from "libs/support";
import { User } from "../../features/user";
import { Button } from "@/components/ui/button";
import { UpsellRocketIcon } from "@/components/ui/icon";

function UnlimitedUsers() {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const openAddModal = () => {
    trackEvent({
      eventName: HEADER_ADD_USER_CLICK,
      useServices: ["AMP"],
    });
    MODAL_OPEN_FUNCTIONS[ADD_USER_MODAL](dispatch);
  };

  return (
    <div>
      <DropdownText>
        <div>Add additional users</div>
        <Flex marginTop={10}>Add users to join Sked Social on your team.</Flex>
      </DropdownText>
      <DropdownMenu.Item>
        <Flex>
          <DropdownButton
            data-testid="unlimited-add-user-button"
            onClick={openAddModal}
          >
            Add Users
          </DropdownButton>
        </Flex>
      </DropdownMenu.Item>
      <ManageUsers justify="center" data-testid="manage-users-link">
        <Link to="/dashboard/users">
          <DropdownMenu.Item>
            <Flex align="center" gap={0}>
              <UsersIcon />
              Manage users
            </Flex>
          </DropdownMenu.Item>
        </Link>
      </ManageUsers>
    </div>
  );
}

function ContributorContent({ adminEmail }: { adminEmail: string }) {
  const openIntercomChat = useOpenIntercomChat("", "accounts");

  return (
    <div data-testid="contributor-can-not-add-user">
      <DropdownText>
        <div>
          <DropdownBoldText>Update permission to add users</DropdownBoldText>
        </div>
        <Flex
          marginTop={10}
        >{`Contact your admin: ${adminEmail} to add users.`}</Flex>
      </DropdownText>
      <Flex marginTop={10}>
        <DropdownButton
          data-testid="add-user-contributor-contact-support"
          className="btn"
          onClick={openIntercomChat}
        >
          Contact Support
        </DropdownButton>
      </Flex>
      <ManageUsers justify="center" data-testid="manage-users-link">
        <Link to="/dashboard/users">
          <Flex align="center" gap={0}>
            <UsersIcon />
            Manage users
          </Flex>
        </Link>
      </ManageUsers>
    </div>
  );
}

function NeedUpgradePlan({ upsell }: { upsell: boolean }) {
  const trackEvent = useTrackEvent();

  const contactSupportText = `I'd like to add more accounts. Can you please help me?`;

  return (
    <div data-testid="need-upgrade-plan-add-user">
      <DropdownText>
        <div>
          <DropdownBoldText>Upgrade required</DropdownBoldText>
        </div>
        <Flex marginTop={10}>
          All plans above Basic include additional users.
        </Flex>
      </DropdownText>

      {upsell ? (
        <UpgradePlanWrap>
          <Button
            variant="blue-primary"
            size="md"
            onClick={() => {
              trackEvent({
                eventName: UPGRADE_USERS_VIEWED,
                useServices: ["AMP", "HS"],
              });
              goToManagePlan();
            }}
          >
            Manage plan
            <UpsellRocketIcon bgColor="#fff" color="#4f31ff" />
          </Button>
        </UpgradePlanWrap>
      ) : (
        <>
          <DropdownButton
            data-testid="add-user-upgrade-contact-support"
            className="btn"
            onClick={() => {
              openSupportChat(contactSupportText, "accounts");
            }}
          >
            Contact Support
          </DropdownButton>
          <ManageUsers justify="center" data-testid="manage-users-link">
            <Link to="/dashboard/users">
              <Flex align="center" gap={0}>
                <UsersIcon />
                Manage users
              </Flex>
            </Link>
          </ManageUsers>
        </>
      )}
    </div>
  );
}

const UpgradePlanWrap = styled.div`
  width: 100% !important;
  margin: 12px auto 0;
`;

export const getOpportunityDataToUseAddUserModal = (user: any) => {
  const isAdmin = user && user.role === "admin";
  const canAddUser = isAdmin || user.preferences?.canManageUsers || false;
  // SKE-3580: gate on the resolved `maximum-users` entitlement, not on a
  // plan-name pattern match. The previous plan-code check returned false
  // for any plan whose slug contained "fundamentals"/"freelancer" — which
  // silently no-op'd the "Add user" modal for reverse-trial users on
  // Basic, even though their entitlement was overridden to Unlimited.
  // `getUserLimit` is the canonical resolver: reverse-trial → "unlimited",
  // entitlement value → numeric, plan-code legacy fallback last.
  const userLimit = getUserLimit(user);
  const acceptUsersFeature: boolean =
    userLimit === "unlimited" ||
    (typeof userLimit === "number" && userLimit > 1);

  return { canAddUser, acceptUsersFeature };
};

export function AddUserMenu(): React.ReactElement {
  const user: User = useSelector(getUser);
  const {
    canAddUser,
    acceptUsersFeature,
  }: { canAddUser: boolean; acceptUsersFeature: boolean } =
    getOpportunityDataToUseAddUserModal(user);

  // NOTE user included self in the collaborators array
  const trackEvent = useTrackEvent();
  const onClickTrigger = () => {
    trackEvent({
      eventName: HEADER_MENU_ADD_USER_CLICK,
      useServices: ["AMP"],
    });
  };

  let dropdownAlignOffset = -10;

  if (!canAddUser || (canAddUser && !acceptUsersFeature)) {
    dropdownAlignOffset = -20;
  }

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <Trigger data-testid="add-user-menu-trigger" onClick={onClickTrigger}>
          <span className="mr-2">+</span>
          Add users
        </Trigger>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenuContent
          asChild
          minWidth={315}
          align="end"
          sideOffset={8}
          alignOffset={dropdownAlignOffset}
        >
          <div>
            {canAddUser && acceptUsersFeature && <UnlimitedUsers />}
            {canAddUser && !acceptUsersFeature && (
              <NeedUpgradePlan upsell={!isLegacyPlan(user)} />
            )}
            {!canAddUser && (
              <ContributorContent adminEmail={user.adminEmail ?? ""} />
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const ManageUsers = styled(Flex)`
  padding: 10px 10px 4px;
  margin: 10px 0 -2px;

  a {
    margin: 0;
    font-weight: 450;

    svg {
      margin-right: 12px;
      position: relative;

      width: 14px;
    }
  }
`;
